import { useState } from "react";
import { Outlet, NavLink } from "react-router-dom";
import { useAuth } from "@/App";
import JarvisPanel from "@/components/RookPanel";
import {
  LayoutDashboard, Library, CheckSquare, Calendar, BarChart3,
  Settings, Menu, X, LogOut, Megaphone, Compass, Radio
} from "lucide-react";

const BRAND_LINKS = [
  { name: "Uncut Gems", url: "https://vault.uncutgems.com" },
  { name: "Pixally", url: "#" },
  { name: "GemVault", url: "https://vault.uncutgems.com" },
];


function BrandBar() {
  return (
    <div className="brand-bar" data-testid="brand-bar">
      {BRAND_LINKS.map((link, i) => (
        <span key={link.name} className="flex items-center">
          {i > 0 && <span className="divider mx-1" />}
          <a href={link.url} target={link.url !== "#" ? "_blank" : undefined} rel="noopener noreferrer">
            {link.name}
          </a>
        </span>
      ))}
      <span className="divider mx-1" />
      <span className="active-brand">CommandPost</span>
    </div>
  );
}

function Sidebar({ open, onClose }) {
  const { logout } = useAuth();

  return (
    <>
      {open && (
        <div className="fixed inset-0 bg-black/60 z-40 md:hidden" onClick={onClose} />
      )}
      <aside className={`sidebar ${open ? "open" : ""}`} data-testid="sidebar">
        <div className="p-5 pb-3">
          <h2 className="font-display text-lg font-bold text-zinc-100 tracking-tight">
            CommandPost
          </h2>
          <p className="section-label mt-1">// command center</p>
        </div>

        <nav className="mt-2 flex flex-col">
          {/* Dashboard */}
          <NavLink
            to="/" end onClick={onClose}
            className={({ isActive }) => `sidebar-nav-item ${isActive ? "active" : ""}`}
            data-testid="nav-dashboard"
          >
            <LayoutDashboard size={18} />
            <span>Dashboard</span>
          </NavLink>

          {/* Social Management section */}
          <div className="mt-4 mb-1 px-5">
            <p className="section-label">// social management</p>
          </div>

          {[
            { path: "/social", icon: Radio, label: "Overview", end: true },
            { path: "/library", icon: Library, label: "Content Library" },
            { path: "/queue", icon: CheckSquare, label: "Approval Queue" },
            { path: "/calendar", icon: Calendar, label: "Calendar" },
            { path: "/analytics", icon: BarChart3, label: "Analytics" },
            { path: "/ads", icon: Megaphone, label: "Ads" },
            { path: "/strategy", icon: Compass, label: "Strategy" },
            { path: "/settings", icon: Settings, label: "Settings" },
          ].map(({ path, icon: Icon, label, end }) => (
            <NavLink
              key={path}
              to={path}
              end={end}
              onClick={onClose}
              className={({ isActive }) =>
                `sidebar-nav-item ${isActive ? "active" : ""}`
              }
              style={{ paddingLeft: "40px" }}
              data-testid={`nav-${label.toLowerCase().replace(/\s+/g, "-")}`}
            >
              <Icon size={16} />
              <span className="text-[13px]">{label}</span>
            </NavLink>
          ))}
        </nav>

        <div className="mt-auto p-5 border-t border-subtle">
          <p className="section-label mb-3">// connected</p>
          <div className="flex items-center gap-2 text-xs text-zinc-500">
            <span className="w-2 h-2 rounded-full bg-status-green" />
            <span>Vimeo</span>
            <span className="ml-auto text-zinc-600">4 brands</span>
          </div>
          <div className="flex items-center gap-2 text-xs text-zinc-500 mt-2">
            <span className="w-2 h-2 rounded-full bg-status-green" />
            <span>YouTube</span>
            <span className="ml-auto text-zinc-600">S&S</span>
          </div>
          <div className="flex items-center gap-2 text-xs text-zinc-500 mt-2">
            <span className="w-2 h-2 rounded-full bg-status-green" />
            <span>Meta Graph API</span>
            <span className="ml-auto text-zinc-600">5 pages</span>
          </div>
          <div className="flex items-center gap-2 text-xs text-zinc-500 mt-2">
            <span className="w-2 h-2 rounded-full" style={{ background: '#a78bfa' }} />
            <span>Uncut Gems</span>
            <span className="ml-auto text-zinc-600">B2B</span>
          </div>
          <div className="flex items-center gap-2 text-xs text-zinc-500 mt-2">
            <span className="w-2 h-2 rounded-full" style={{ background: '#ec4899' }} />
            <span>Pixally</span>
            <span className="ml-auto text-zinc-600">pre-launch</span>
          </div>
        </div>

        <div className="p-5 border-t border-subtle">
          <button
            onClick={logout}
            className="sidebar-nav-item w-full text-zinc-500 hover:text-red-400 px-0"
            data-testid="logout-btn"
          >
            <LogOut size={16} />
            <span>Sign Out</span>
          </button>
        </div>
      </aside>
    </>
  );
}

export default function Layout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);

  return (
    <div className="min-h-screen bg-[#0f1114]">
      <BrandBar />
      <Sidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} />

      <button
        className="fixed top-[40px] left-4 z-50 md:hidden p-2 rounded-xl bg-studio-raised border border-[#2a2e36] text-zinc-400"
        onClick={() => setSidebarOpen(!sidebarOpen)}
        data-testid="mobile-menu-btn"
      >
        {sidebarOpen ? <X size={20} /> : <Menu size={20} />}
      </button>

      <main className="main-content" data-testid="main-content">
        <Outlet />
      </main>

      <JarvisPanel />
    </div>
  );
}
